import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { SearchResult } from '@df/search/search-result';
import { NToggle } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { IlinkListItem } from 'projects/app-default/src/app/common/link-list1/link-list1.component';
import { NPageSearch } from '../page-search.interfaces';
import { PageSearchService } from '../page-search.service';

@Component({
    selector: 'page-search-outlet',
    templateUrl: 'page-search-outlet.html',
    styleUrls: ['page-search-outlet.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageSearchOutletComponent>({
    id: NPageSearch.OUTLET_BLOCK_ID,
    block: {
        url: NPageSearch.OUTLET_BLOCK_ID,
        scope: 'website',
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Search outlet',
    description: 'Header search outlet content',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'popular-searches',
            title: 'Popular searches'
        },
        {
            key: 'popularSearchesTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Popular Searches'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'feature-links',
            title: 'Feature Links'
        },
        {
            key: 'featureLinksTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Lorm ipsum'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'featureLinks',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Links',
            itemName: 'Link',
            itemNameField: 'label',
            fields: [
                {
                    key: 'label',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    default: 'Lorem',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    default: '/'
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image'
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'noResult',
            title: 'No result message'
        },
        {
            key: 'noResultTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            description: 'Use % to mark where users search term should be displayed ',
            default: 'Lorm ipsum'
        },
        {
            key: 'noResultSubtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            description: 'Use % to mark where users search term should be displayed ',
            default: 'Lorm ipsum'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'trending',
            title: 'Content cards'
        },
        {
            key: 'contentCardsTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Trending'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'contentCards',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Cards',
            itemName: 'Item',
            itemNameField: 'title',
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Lorem ipsum dolor',
                    required: true
                },
                {
                    key: 'buttonLabel',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Button label',
                    default: 'Find out more',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    default: '/',
                    required: true
                }
            ],
            defaultSize: 3,
            maxSize: 4
        }
    ]
})
export class PageSearchOutletComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageSearch.IBlockDataContent;

    /**
     * Currently visible search result
     */
    result?: SearchResult;

    /**
     * ids for tab switching on mobile
     */
    private readonly tabGroup = 'pageSearchOutletTabs';
    readonly termsId: NToggle.TId = [this.tabGroup, 'terms'];
    readonly postsId: NToggle.TId = [this.tabGroup, 'posts'];

    constructor(
        private searchService: KlevuSearchService,
        private pageSearchService: PageSearchService
    ) {
        super();
    }

    /**
     * Initialization
     */
    override ngOnInit() {
        this.searchService.loadKlevuVars(() => this.detectChanges());

        this.subscriptions.add(
            this.pageSearchService.state.subscribe(result => {
                if (this.result !== result) {
                    this.result = result;
                    this.detectChanges();
                }
            })
        );
        super.ngOnInit();
    }

    /**
     * Go to search view page
     */
    gotToSearchView() {
        this.pageSearchService.gotToSearchView(this.result?.term);
    }

    /**
     * If we do show search results (or init screen)
     */
    get isSearchResult(): boolean {
        return !!this.result;
    }

    /**
     * If suggested terms can be shown
     */
    get canShowTerms(): boolean {
        return !!(this.result?.terms && this.result?.terms.length);
    }

    /**
     * If side content from cms should be shown
     */
    get canShowCmsSideContent(): boolean {
        return !this.canShowTerms;
    }

    /**
     * If side content from cms should be shown
     */
    get canShowPopularSerches(): boolean {
        return this.canShowCmsSideContent && this.popularSearchesAsLinks.length > 0;
    }

    /**
     * If product results can be shown
     */
    get canShowProductResults(): boolean {
        return !!this.result?.hasProducts;
    }

    /**
     * Search terms normalized to link list format
     */
    get termsAsLinks(): IlinkListItem[] {
        return this.result?.terms?.map(i => ({ label: i.barePhrase, link: i.redirect || `/search/${i.barePhrase}` })) || [];
    }

    /**
     * Popular searches
     */
    get popularSearchesAsLinks(): IlinkListItem[] {
        return this.searchService.webstorePopularTerms?.map(i => ({ label: i, link: `/search/${i}` })) || [];
    }

    /**
     * Pages normalized to link list format
     */
    get pagesAsLinks(): IlinkListItem[] {
        return this.result?.pages?.map(i => ({ label: i.name, link: i.data?.url || '', image: (i.data as IPageDataV2).image })) || [];
    }

    /**
     * Search term
     */
    get term(): string {
        return this.result?.term || '';
    }

    /**
     * No results messaging
     */
    get noResultTitle(): string {
        if (this.content && this.content.noResultTitle) {
            return this.content.noResultTitle.replace('%', this.term);
        }
        return '';
    }

    get noResultSubtitle(): string {
        if (this.content && this.content.noResultSubtitle) {
            return this.content.noResultSubtitle.replace('%', this.term);
        }
        return '';
    }

    get isNoResult(): boolean {
        return (
            !!this.result?.data &&
            this.result.data.result?.product?.length === 0 &&
            this.result.data.result?.category?.length === 0 &&
            this.result.data.result?.page?.length === 0
        );
    }

    trackByIdFn(index: number, model: Product<IProductDataV2>) {
        return model.id;
    }
}
